import './new.note.create.form.scss';

import { Box, Collapse } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { FormTextField, NewConvergeButton } from '../../presentation';
import {
  NoteInput,
  NoteTargetName,
  NoteTargetType,
  useUpsertNoteMutation,
} from '@converge/graphql';

import { Stack } from '@mui/system';
import { useConvergeSnackBar } from '../../contexts';
import { normalizeNotes } from '@converge/utils';

interface INewNoteCreateFormProps extends Partial<NoteInput> {
  showBtn?: boolean | undefined;
  onClose?: () => void;
  callHandleUpdateRenewalType?: () => void;
  disabled?: boolean;
  inputTestId?: string;
  buttonTestId?: string;
}

export const NewNoteCreateForm = ({
  notes = '',
  targetArea,
  targetType = NoteTargetType.Relationship,
  targetName = NoteTargetName.HasCoverageType,
  targetUuid,
  showBtn = false,
  onClose,
  callHandleUpdateRenewalType,
  disabled,
  targetSubUuid,
  inputTestId,
  buttonTestId,
  ...props
}: INewNoteCreateFormProps) => {
  const { notify } = useConvergeSnackBar();

  const { upsertNote, loading } = useUpsertNoteMutation({
    onError: (error) =>
      notify(`Failed to add note: ${error.message}`, {
        variant: 'error',
      }),
    onCompleted: () => {
      notify('Note has been added', {
        variant: 'success',
      });
    },
  });
  const formMethods = useForm<NoteInput>({
    defaultValues: {
      notes: normalizeNotes(notes || ''),
      targetArea,
      targetType: targetArea ? undefined : targetType,
      targetUuid: targetUuid,
      targetName: targetArea ? undefined : targetName,
      targetSubUuid,
      ...props,
    },
    shouldUseNativeValidation: false,
  });

  const formNotes = formMethods.watch('notes');

  return (
    <>
      <FormProvider {...formMethods}>
        <form
          onSubmit={formMethods.handleSubmit(async (details) => {
            const sanitizedDetails = {
              ...details,
              notes: normalizeNotes(details.notes || ''),
            };
            await upsertNote({ variables: { details: sanitizedDetails } });
            formMethods.setValue('notes', '');
            callHandleUpdateRenewalType && callHandleUpdateRenewalType();
            onClose && onClose();
          })}
        >
          <Collapse in={!loading} unmountOnExit>
            <Stack marginBottom={2} className="note-broker-form-text-field">
              <FormTextField
                disabled={disabled}
                isRichText
                hiddenLabel
                name="notes"
                label="Notes"
                multiline
                rows={4}
                containerWidth={12}
                dataTestId={inputTestId}
              />
            </Stack>
          </Collapse>
          <Box className="note-btn-container">
            <Box className="create-node-btn-container">
              <NewConvergeButton
                label={'Save Note'}
                type="submit"
                disabled={!formNotes}
                loading={loading}
                dataTestId={buttonTestId}
                style={{
                  width: '40px',
                }}
              />
            </Box>
          </Box>
        </form>
      </FormProvider>
    </>
  );
};
